<template>
  <form class="form" @submit="onSubmit">
    <div
      v-for="({ name, placeholder, type, rules, label }, i) in formFields"
      :key="`${name}-${i}`"
      class="field"
      :class="{
        'field--checkbox': isCheckbox(type)
      }"
    >
      <FormCheckbox
        v-if="isCheckbox(type)"
        :label="getLabel(rules, label)"
        :name="name"
        checked-value
      />
      <FormText
        :is="componentType(type)"
        v-else
        :label="getLabel(rules, label)"
        :type="type"
        :name="name"
        :placeholder="placeholder"
      />
    </div>
    <div class="button-wrap">
      <span
        v-if="!hideRuleText"
        class="rule-text t-form-label"
        v-text="t('form.form-rule')"
      />
      <BaseButton
        class="button"
        :color="buttonColor"
        size="l"
        type="submit"
        :disabled="isLoading"
        :loading="isLoading"
        :text="buttonText"
      />
      <FormError v-if="!meta.touched" :error-message="formError" />
    </div>
  </form>
</template>

<script lang="ts" setup>
import { required, email, max } from '@vee-validate/rules'
import { useForm, defineRule } from 'vee-validate'

const { t } = useI18n()

defineRule(
  'required',
  (value: string): boolean | string => required(value) || t('form.required')
)

defineRule(
  'max-length-255',
  (value: string): boolean | string =>
    max(value, [255]) || t('form.incorrect-message')
)

defineRule(
  'email',
  (value: string): boolean | string => email(value) || t('form.incorrect-email')
)

type InputType = {
  label: string
  name: string
  rules?: string
  placeholder?: string
  type?: string
}

const props = withDefaults(
  defineProps<{
    formFields: InputType[]
    isLoading: boolean
    formError?: string
    hideRuleText?: boolean
    buttonColor?: 'primary' | 'outline-black'
    buttonText?: string
  }>(),
  {
    isLoading: false,
    formError: '',
    hideRuleText: false,
    buttonColor: 'outline-black',
    buttonText: ''
  }
)

type Schema = {
  [key: string]: string
}

const schema = props.formFields.reduce<Schema>((acc, field) => {
  if (field.rules) acc[field.name] = field.rules
  return acc
}, {})

const emit = defineEmits(['submit'])

const { handleSubmit, resetForm, meta } = useForm({
  validationSchema: schema
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GenericObject = Record<string, any>
const onSubmit = handleSubmit(
  (formData: GenericObject, formActions?: GenericObject) => {
    emit('submit', formData, formActions)
    resetForm()
  }
)
const isCheckbox = (type?: string) => type === 'checkbox'
const componentType = (type?: string) =>
  type === 'textarea' ? 'textarea' : 'input'
const buttonText = computed(() =>
  props.isLoading ? t('form.submitting') : props.buttonText || t('subscribe')
)
const getLabel = (rules: InputType['rules'], label: InputType['label']) =>
  rules && rules.includes('required') ? `${label}*` : label
</script>

<style lang="scss" scoped>
.form {
  display: flex;
  flex-direction: column;
}

.button-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  margin-top: rem(6px);
}

.button {
  margin-right: auto;
}

.rule-text {
  margin-bottom: rem(4px);
}
</style>
